/**
 * Hook personalizado para escuchar en tiempo real cambios en la tabla interaction_counter.
 * Se suscribe a cambios en las columnas del contador de interacciones del usuario actual.
 * Implementa patrón singleton para evitar múltiples suscripciones y anti-duplicación de eventos.
 * 
 * @param {Object} options - Opciones de configuración
 * @param {function} options.onCounterChange - Callback cuando hay un cambio en el contador
 * @param {function} options.onVisitasChange - Callback específico para cambios en visitas
 * @param {function} options.onMeGustasChange - Callback específico para cambios en me gustas
 * @param {function} options.onMensajesChange - Callback específico para cambios en mensajes
 * @returns {Object} - Objeto con estado de la suscripción
 */

import { useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

// Variable global para almacenar el canal por usuario (patrón singleton)
const globalCounterChannels = new Map();

// Variable global para tracking de eventos procesados (anti-duplicación)
const processedCounterEvents = new Set();

// Función de limpieza de eventos procesados (limpiar cada 30 segundos)
setInterval(() => {
  if (processedCounterEvents.size > 50) {
    console.log('[useInteractionCounterRealtime] Limpiando eventos procesados:', processedCounterEvents.size);
    processedCounterEvents.clear();
  }
}, 30000);

export const useInteractionCounterRealtime = (options = {}) => {
  const { 
    onCounterChange, 
    onVisitasChange, 
    onMeGustasChange, 
    onMensajesChange 
  } = options;
  
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.id;
  
  // Ref para identificar este componente específico
  const componentIdRef = useRef(`counter-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`);

  // Refs para callbacks para evitar dependencias en useEffect
  const callbacksRef = useRef({
    onCounterChange,
    onVisitasChange,
    onMeGustasChange,
    onMensajesChange
  });

  // Actualizar refs cuando cambian los callbacks
  useEffect(() => {
    callbacksRef.current = {
      onCounterChange,
      onVisitasChange,
      onMeGustasChange,
      onMensajesChange
    };
  }, [onCounterChange, onVisitasChange, onMeGustasChange, onMensajesChange]);

  /**
   * Callback principal para manejar cambios en interaction_counter
   * Implementa anti-duplicación y debounce para invalidación de queries
   */
  const handleCounterChange = useCallback((payload) => {
    // Crear ID único para este evento específico usando timestamp y datos del payload
    const timestamp = payload.new?.updated_at || payload.old?.updated_at || Date.now();
    const recordId = payload.new?.user_id || payload.old?.user_id || 'unknown';
    const eventId = `${userId}-counter-${recordId}-${timestamp}`;
    
    // Evitar procesar el mismo evento múltiples veces
    if (processedCounterEvents.has(eventId)) {
      console.log('[useInteractionCounterRealtime] Evento duplicado ignorado:', eventId);
      return;
    }
    
    // Marcar evento como procesado
    processedCounterEvents.add(eventId);
    
    console.log('[useInteractionCounterRealtime] Cambio en contador detectado - Componente:', componentIdRef.current, 'Evento:', eventId);
    console.log('[useInteractionCounterRealtime] Payload:', payload);
    
    // Detectar qué columnas cambiaron
    const oldData = payload.old || {};
    const newData = payload.new || {};
    
    const changes = {};
    
    if (oldData.visitas !== newData.visitas) {
      changes.visitas = {
        old: oldData.visitas,
        new: newData.visitas
      };
      if (callbacksRef.current.onVisitasChange) {
        callbacksRef.current.onVisitasChange(newData.visitas, oldData.visitas);
      }
    }
    
    if (oldData.me_gustas !== newData.me_gustas) {
      changes.me_gustas = {
        old: oldData.me_gustas,
        new: newData.me_gustas
      };
      if (callbacksRef.current.onMeGustasChange) {
        callbacksRef.current.onMeGustasChange(newData.me_gustas, oldData.me_gustas);
      }
    }
    
    if (oldData.mensajes !== newData.mensajes) {
      changes.mensajes = {
        old: oldData.mensajes,
        new: newData.mensajes
      };
      if (callbacksRef.current.onMensajesChange) {
        callbacksRef.current.onMensajesChange(newData.mensajes, oldData.mensajes);
      }
    }
    
    // Invalidar queries relacionadas con el contador de visitas
    // Usamos un timeout pequeño para agrupar múltiples invalidaciones
    // Solo invalidar si realmente hubo cambios en los datos
    if (Object.keys(changes).length > 0) {
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['visitCount'] });
        queryClient.invalidateQueries({ queryKey: ['interactionCounter'] });
        
        // También invalidar queries relacionadas con el tabeador si hay cambios
        if (changes.visitas || changes.me_gustas) {
          queryClient.invalidateQueries({ queryKey: ['tabeador_count'] });
        }
        
        console.log('[useInteractionCounterRealtime] Queries invalidadas por cambios:', Object.keys(changes));
      }, 100);
    }
    
    // Llamar al callback general si existe
    if (callbacksRef.current.onCounterChange) {
      callbacksRef.current.onCounterChange(changes, newData, oldData);
    }
  }, [queryClient, userId]);

  /**
   * Efecto principal para gestionar suscripciones en tiempo real
   * Implementa patrón singleton para evitar múltiples suscripciones por usuario
   */
  useEffect(() => {
    if (!userId) {
      console.log('[useInteractionCounterRealtime] Usuario no autenticado');
      return;
    }

    const componentId = componentIdRef.current;
    console.log('[useInteractionCounterRealtime] Montando componente', componentId, 'para usuario', userId);

    // Si no existe canal global para este usuario, crearlo
    if (!globalCounterChannels.has(userId)) {
      console.log('[useInteractionCounterRealtime] Creando canal global para usuario:', userId);
      
      const channel = supabase
        .channel(`user-interaction-counter-${userId}`)
        .on('postgres_changes', {
          event: '*', // Escuchar INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'interaction_counter',
          filter: `user_id=eq.${userId}` // Solo cambios del usuario actual
        }, handleCounterChange)
        .subscribe((status) => {
          console.log('[useInteractionCounterRealtime] Status suscripción counter', userId, ':', status);
          
          if (status === 'SUBSCRIBED') {
            console.log('[useInteractionCounterRealtime] Realtime conectado para interaction_counter');
          } else if (status === 'CHANNEL_ERROR') {
            console.error('[useInteractionCounterRealtime] Error en canal Realtime');
          } else if (status === 'TIMED_OUT') {
            console.warn('[useInteractionCounterRealtime] Timeout en canal Realtime');
          }
        });

      // Guardar referencia al canal y contador de componentes
      globalCounterChannels.set(userId, {
        channel,
        componentCount: 1,
        componentIds: new Set([componentId])
      });
    } else {
      // Incrementar contador de componentes usando este canal
      const channelData = globalCounterChannels.get(userId);
      channelData.componentCount++;
      channelData.componentIds.add(componentId);
      console.log('[useInteractionCounterRealtime] Canal existente para', userId, '. Componentes activos:', channelData.componentCount);
    }

    // Función de limpieza
    return () => {
      console.log('[useInteractionCounterRealtime] Desmontando componente', componentId);
      
      const channelData = globalCounterChannels.get(userId);
      if (channelData) {
        channelData.componentCount--;
        channelData.componentIds.delete(componentId);
        
        console.log('[useInteractionCounterRealtime] Componentes restantes para', userId, ':', channelData.componentCount);
        
        // Si no hay más componentes usando este canal, limpiarlo
        if (channelData.componentCount <= 0) {
          console.log('[useInteractionCounterRealtime] Limpiando canal para usuario:', userId);
          supabase.removeChannel(channelData.channel);
          globalCounterChannels.delete(userId);
        }
      }
    };
  }, [userId, handleCounterChange]);

  return {
    isSubscribed: !!userId && globalCounterChannels.has(userId),
    userId,
    componentId: componentIdRef.current,
  };
};
